import { useEffect, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useRegistrationStore } from '../../stores/registrationStore'
import { Loader2 } from 'lucide-react'
import { analyticsService } from '../../services/analyticsService'
import { AnalyticsEvents } from '../../types/analytics'

// Use AUTH_API_URL for onboarding/subscription endpoints
const AUTH_API_URL =
  import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'

const CheckoutPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { registrationData } = useRegistrationStore()
  const { isAuthenticated, isLoading: authLoading } = useAuthStore()

  useEffect(() => {
    // Wait for auth check to complete before making decisions
    if (authLoading) {
      return
    }

    // NOTE: CheckoutPage does NOT require authentication check here
    // Authenticated users with 'verified_unpaid' onboardingStatus are redirected here
    // The Stripe checkout session creation endpoint on the backend will handle auth

    // Guard: Ensure we have registration data (tierId required for checkout)
    if (!registrationData || !registrationData.selectedTierId) {
      window.location.href = '/pricing'
      return
    }

    createCheckoutSession()
  }, [isAuthenticated, authLoading, registrationData])

  const createCheckoutSession = async () => {
    try {
      setLoading(true)
      setError(null)

      // Track checkout started
      analyticsService.track(AnalyticsEvents.SUBSCRIPTION_CHECKOUT_STARTED, {
        tierId: registrationData?.selectedTierId,
        billingCycle: registrationData?.billingCycle || 'monthly',
      })

      const response = await fetch(`${AUTH_API_URL}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important for authentication
        body: JSON.stringify({
          tierId: registrationData?.selectedTierId,
          billingCycle: registrationData?.billingCycle || 'monthly',
          successUrl: `${window.location.origin}/onboarding/success`,
          cancelUrl: `${window.location.origin}/onboarding/payment-canceled`
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message || 'Failed to create checkout session'
        )
      }

      const data = await response.json()

      // Redirect to Stripe Checkout
      if (data.success && data.data?.checkoutUrl) {
        window.location.href = data.data.checkoutUrl
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (err: any) {
      console.error('Checkout error:', err)
      setError(err.message || 'Failed to initiate checkout')
      setLoading(false)
    }
  }

  const handleRetry = () => {
    createCheckoutSession()
  }

  const handleCancel = () => {
    // Track checkout canceled
    analyticsService.track(AnalyticsEvents.SUBSCRIPTION_CHECKOUT_CANCELED, {
      tierId: registrationData?.selectedTierId,
    })
    window.location.href = '/onboarding/payment-canceled'
  }

  if (authLoading || (loading && !error)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50'>
        <div className='text-center'>
          <Loader2 className='w-10 h-10 text-avi-primary animate-spin mx-auto mb-3' />
          <p className='text-sm sm:text-base text-gray-600'>
            {authLoading
              ? 'Verifying authentication...'
              : 'Preparing your checkout...'}
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 px-4'>
        <div className='bg-white p-5 sm:p-6 rounded-lg shadow-lg max-w-sm w-full'>
          <div className='text-center'>
            <div className='text-red-500 text-3xl sm:text-4xl mb-3'>⚠️</div>
            <h2 className='text-lg sm:text-xl font-bold text-gray-900 mb-2'>
              Checkout Error
            </h2>
            <p className='text-sm text-gray-600 mb-5'>{error}</p>
            <div className='space-y-2'>
              <button
                onClick={handleRetry}
                className='w-full bg-avi-primary text-white text-sm px-4 py-2 rounded-lg hover:opacity-90 transition'
              >
                Try Again
              </button>
              <button
                onClick={handleCancel}
                className='w-full bg-gray-200 text-gray-700 text-sm px-4 py-2 rounded-lg hover:bg-gray-300 transition'
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default CheckoutPage
